/**
 * Agent detail-pane faces — agent slug → the agent's INTERACTIVE surface,
 * rendered in the Home detail pane so the agent works IN the frame (the live
 * matrix / board / planner) instead of a read-only summary. The dispatcher
 * (ConversationOutput) looks a session's `agent_slug` up here: a hit renders
 * the live face, a miss falls back to the read-only representation (markdown
 * answer + native blocks).
 *
 * Every agent has TWO representations and ONE implementation: in-frame (here)
 * and full-screen (its own sub-app at its subdomain). Both mount the same
 * component, so they cannot drift.
 *
 * DISCOVERED, NOT REGISTERED. Faces come from globbing `apps/<slug>/src/face.tsx`
 * and keying on the directory name — the same filesystem discovery
 * frontend/Dockerfile.local and build.yml already use to decide which apps to
 * build and deploy. A hand-maintained slug→component map meant the platform
 * carried a list of which agents exist, so an agent could not be added,
 * renamed or retired without editing platform code — the opposite of agents
 * being user data (ADR-001).
 *
 * ADDING A NEW AGENT'S LIVE SURFACE: create `apps/<slug>/src/face.tsx` with a
 * default export taking AgentFaceProps. Nothing here changes.
 *
 * The MAIN-APP agents below are the exception that proves the rule: the agents
 * in CHAT_FACE_SLUGS have no sub-app of their own — the main app IS their
 * surface — so their face is the shared chat view rather than a module under
 * apps/.
 */
import type { ComponentType } from 'react'

import {
  agentSubdomain,
  ALLOCATION_AGENT_SLUG,
  CHAT_AGENT_SLUG,
  HANDOVER_AGENT_SLUG,
  REPORT_AGENT_SLUG,
} from '@/lib/sessionRouting'
import { ChatConnected } from '@/components/chat/ChatConnected'
import { AllocationBoardConnected } from '@/components/allocation/AllocationBoardConnected'
import type { AgentFaceProps } from './agentFace'
import { agentSlugForDir, slugFromAppModulePath } from './appModulePath'
import { remoteAgentFace } from './RemoteAgentFace'

/**
 * Historic name for AgentFaceProps, kept as an alias so the board wrappers and
 * Home.tsx keep compiling against one type. The canonical definition lives in
 * ./agentFace, which app modules import — they must not pull this file, or an
 * app would bundle every other agent's face.
 */
export type AgentDetailViewProps = AgentFaceProps

// Faces discovered from the app modules. Vite requires a LITERAL glob, so the
// pattern cannot be built from a variable — that is fine: the pattern is the
// convention. `eager` because Home renders a face synchronously when a session
// is selected; a lazy import would flash the read-only fallback first.
const DISCOVERED_FACES = import.meta.glob<{ default: ComponentType<AgentFaceProps> }>(
  '../../../../apps/*/src/face.tsx',
  { eager: true },
)

// apps/<dir>/src/face.tsx  →  <dir>
// Exported for agentFaces.test.ts: a silently-wrong slug is the failure mode
// that turns an agent read-only with no error anywhere.
export function slugFromFacePath(path: string): string {
  return slugFromAppModulePath(path, 'face.tsx')
}

/**
 * Agents whose face is the shared chat view — no sub-app, so the main app is
 * both their full-screen surface and their frame: `chat` itself, `handover`
 * (a conversation with sign-off cards) and `report` (drafts a care report from
 * the patient's record, raising one documentation gap at a time).
 *
 * This list is the SOURCE. The registry below is built from it and
 * faceRequiresSession answers from it, so the two cannot disagree. They did:
 * `report` was added to the registry (#2599) while Home.tsx separately
 * hand-listed the other two to decide which faces open session-less, and the
 * mismatch mounted the chat view with no session — `session.id` on its first
 * line — which the ErrorBoundary turned into a permanently broken `/home`,
 * since the open face is persisted. Adding a fourth chat agent is one line
 * here and nothing anywhere else.
 */
export const CHAT_FACE_SLUGS: readonly string[] = [
  CHAT_AGENT_SLUG,
  HANDOVER_AGENT_SLUG,
  REPORT_AGENT_SLUG,
]

// Exported so agentFaces.test.ts can assert discovery actually produced
// something. A glob that resolves to nothing yields an empty map, every agent
// falls back to the read-only view, and nothing errors — the whole point of the
// test is that this map can never quietly be empty.
export const AGENT_FACES: Record<string, ComponentType<AgentFaceProps>> = {
  // Main-app agents whose face is the shared chat view, from the one list
  // above — spread so the map cannot hold a chat face the list does not name.
  ...Object.fromEntries(CHAT_FACE_SLUGS.map((slug) => [slug, ChatConnected])),
  // The Schichtstart board works IN the frame and has no sub-app to open
  // full-screen, so the main app is its only surface.
  [ALLOCATION_AGENT_SLUG]: AllocationBoardConnected,
  // Keyed on the agent's DECLARED slug, not the directory — see
  // appModulePath.ts's agentSlugForDir.
  ...Object.fromEntries(
    Object.entries(DISCOVERED_FACES).map(([path, mod]) => [
      agentSlugForDir(slugFromFacePath(path)),
      mod.default,
    ]),
  ),
}

/** Slugs whose face is bundled by the main app because they have no sub-app. */
export const MAIN_APP_FACE_SLUGS = [...CHAT_FACE_SLUGS, ALLOCATION_AGENT_SLUG]

/** Declared agent slugs discovered from apps/<dir>/src/face.tsx. */
export const DISCOVERED_FACE_SLUGS = Object.keys(DISCOVERED_FACES)
  .map((path) => agentSlugForDir(slugFromFacePath(path)))
  .sort()

/**
 * The interactive detail view for an agent, or null to use the read-only
 * fallback: the face this bundle carries, else, for an agent whose app is its
 * own deployed service (it declares a subdomain and ships no face here), that
 * app in a frame (RemoteAgentFace).
 */
export function getAgentDetailView(
  agentSlug: string | undefined,
): ComponentType<AgentFaceProps> | null {
  if (!agentSlug) return null
  const bundled = AGENT_FACES[agentSlug]
  if (bundled) return bundled
  return agentSubdomain(agentSlug) ? remoteAgentFace(agentSlug) : null
}

/**
 * Does this agent's face only work BOUND to a session, so a session-LESS open
 * (Home's persisted agent face) has to use the deferred chat surface instead of
 * mounting the registered view?
 *
 * Every discovered app face accepts `session?` and opens at its agent's root
 * screen with none. The shared chat view does not: its first line reads
 * `session.id`. Answered from CHAT_FACE_SLUGS — the same list the registry is
 * built from — rather than by recognising the component, which a later
 * `memo()` or `forwardRef()` around the export would silently defeat.
 */
export function faceRequiresSession(agentSlug: string | undefined): boolean {
  return agentSlug != null && CHAT_FACE_SLUGS.includes(agentSlug)
}
